import { addSpotifyAttribution, isSpotifyDeepLinkUrl } from 'shared/util/gif-maker-util'
import { SourceEditorEdits } from '../components/source-editor/types'
import { CreationToolUploadTag } from '../components/upload/types'
import { CreationToolSourceFile } from '../types'
import CreationToolOutputType from '../types/output-type'
import getUploadWithEdits from './get-upload-with-edits'

export type UploadData = {
    apiKey: string
    tags: CreationToolUploadTag[]
    rating: string
    title?: string
    description?: string
    sourceUrl?: string
    channelId?: string
    username?: string
    userToken: string
    creationLocation: string
    outputType?: CreationToolOutputType
    isPublic: boolean
    ctaText?: string
    edits?: SourceEditorEdits
}

const convertToFormData = (data: object): FormData => {
    const formData = new FormData()
    for (let key in data) {
        formData.append(key, data[key])
    }
    return formData
}

const forceUrlProtocol = (url: string): string => {
    if (url && url.search(/^http[s]?\:\/\//) === -1) {
        return `https://${url}`
    }
    return url
}

export const uploadGif = async (
    uploadUrl: string,
    source: CreationToolSourceFile,
    uploadData: UploadData
): Promise<string> => {
    const {
        apiKey,
        outputType = CreationToolOutputType.Gif,
        userToken,
        tags,
        rating,
        title,
        description,
        sourceUrl = '',
        channelId = '',
        username = '',
        creationLocation,
        isPublic,
        ctaText,
        edits,
    } = uploadData

    if (!userToken) {
        throw `You must provide a user token to upload.`
    }

    const sourceWithEdits: File | string = await getUploadWithEdits(source, outputType, edits)
    const sourceUrlClean = forceUrlProtocol(sourceUrl)

    let data: any = {
        source_post_url: sourceUrlClean,
        tags: tags.map((t) => t.text).join(','),
        is_hidden: isPublic ? 0 : 1,
        channel_id: channelId,
        rating,
        username,
        creation_location: creationLocation,
    }
    let isVideo = false

    // set the source url or file
    if (typeof sourceWithEdits === 'string') {
        data.source_image_url = sourceWithEdits
    } else {
        data.file = sourceWithEdits
    }

    // if uploading video, set video data
    if (outputType === CreationToolOutputType.Video) {
        data = {
            ...data,
            title: title || '',
            description: description || '',
            hidden: data.is_hidden, // video endpoint uses hidden not is_hidden
            sync: 1, // tells server to keep connection open instead of polling
        }
        isVideo = true
    }

    if (sourceUrlClean && ctaText) {
        let updatedSourceUrl = sourceUrlClean
        if (isSpotifyDeepLinkUrl(sourceUrlClean)) {
            updatedSourceUrl = addSpotifyAttribution(sourceUrlClean)
        }
        data.cta = JSON.stringify({
            text: ctaText,
            link: updatedSourceUrl,
        })
    }

    // tag originators
    const tagsWithOriginators = tags.filter((t) => !!t.originator)
    if (tagsWithOriginators.length > 0) {
        data.tag_originator = JSON.stringify(
            tagsWithOriginators.reduce((obj, item) => ({ ...obj, [item.text]: item.originator }), {})
        )
    }

    const { data: response, meta } = await fetch(`${uploadUrl}${isVideo ? '/video' : ''}?api_key=${apiKey}`, {
        method: 'POST',
        headers: {
            Accept: '*/*',
            Authorization: `Token ${userToken}`,
        },
        body: convertToFormData(data),
    }).then((response) => response.json())

    if (meta && !response) {
        throw new Error(meta.description || '')
    }

    return response.id
}
